import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Activity, Boxes, Container, Clock3, Info, Users } from "lucide-react";
import type { AdminToolbar } from "./RuntimePortalPage";
import { getCachedPortalBootstrap, getPortalBootstrapCacheKey, setCachedPortalBootstrap } from "../portalRuntimeCache";
import { getCachedAnalyticsFilterOptions, getCachedAnalyticsOverview, setCachedAnalyticsFilterOptions, setCachedAnalyticsOverview } from "../portalPageDataCache";
import ShipmentFilterBar, { type ShipmentFilterBarFilter } from "../components/ShipmentFilterBar";

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

type RuntimeCustomer = {
  customerId: string;
  customerKey?: string;
  customerName?: string;
};

type RuntimeBootstrap = {
  role?: string;
  canSelectCustomer?: boolean;
  selectedCustomer?: RuntimeCustomer | null;
  customers?: RuntimeCustomer[];
  enabledPages?: { shipments?: boolean; analytics?: boolean; jpc?: boolean; admin?: boolean };
};

type Carrier = {
  carrierName?: string;
  shipmentCount?: number;
  containerCount?: number;
  avgTransitDays?: number;
  onTimeDeparturePct?: number;
  onTimeEtaPct?: number;
};

type Lane = {
  carrierName?: string;
  originPort?: string;
  destinationPort?: string;
  shipmentCount?: number;
  containerCount?: number;
  grossWeight?: number;
  volume?: number;
};

type Overview = {
  executive?: {
    totalShipments?: number;
    totalContainers?: number;
    totalCarriers?: number;
    avgTransitDays?: number;
    totalGrossWeight?: number;
    totalVolume?: number;
  };
  carrierPerformance?: Carrier[];
  volumeAnalysis?: Lane[];
};

// Keeps navigation between the two portal pages responsive without persisting
// customer data beyond the current browser session.
type Props = {
  apiFetch: ApiFetch;
  initialCustomerId?: string;
  onSelectedCustomerChange?: (customerId: string) => void;
  onPortalContext?: (context: { role?: string; customerId?: string; enabledPages?: { shipments?: boolean; analytics?: boolean; jpc?: boolean; bookings?: boolean; quotes?: boolean; admin?: boolean } }) => void;
  onAccessDenied?: () => void;
  onAdminToolbarChange?: (toolbar: AdminToolbar) => void;
};

const int = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });
const decimal = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 1 });

function average(values: number[]) {
  return values.length ? values.reduce((total, value) => total + value, 0) / values.length : 0;
}

function parseFilterValues(value: string | null): string[] {
  return String(value || "").split("|").map((item) => item.trim()).filter(Boolean);
}

function MetricInfo({ children }: { children: React.ReactNode }) {
  return <span className="group relative inline-flex"><button type="button" aria-label="Show calculation details" className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-400 outline-none transition hover:text-orange-500 focus-visible:ring-2 focus-visible:ring-orange-400"><Info className="h-3.5 w-3.5" aria-hidden="true" /></button><span role="tooltip" className="pointer-events-none absolute left-0 top-full z-30 mt-2 hidden w-64 rounded-md bg-slate-950 px-3 py-2 text-left text-[11px] font-medium normal-case leading-relaxed tracking-normal text-white shadow-xl group-hover:block group-focus-within:block dark:bg-slate-700">{children}</span></span>;
}

function Kpi({ label, value, Icon, accent, calculation }: { label: string; value: string; Icon: React.ElementType; accent: string; calculation: string }) {
  return (
    <div className="min-w-[132px] bg-white px-5 py-4 dark:bg-slate-900/50 sm:min-w-[150px]">
      <div className="flex items-center gap-2"><Icon className="h-3.5 w-3.5 shrink-0" style={{ color: accent }} /><p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">{label}</p><MetricInfo>{calculation}</MetricInfo></div>
      <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">{value}</p>
    </div>
  );
}

function Panel({ title, children, calculation, className = "" }: { title: string; children: React.ReactNode; calculation: string; className?: string }) {
  return (
    <section className={`min-w-0 border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/50 ${className}`}>
      <div className="border-b border-slate-200 px-5 py-3 dark:border-slate-800">
        <div className="flex items-center gap-2"><h2 className="text-sm font-semibold text-slate-900 dark:text-white">{title}</h2><MetricInfo>{calculation}</MetricInfo></div>
      </div>
      {children}
    </section>
  );
}

function Fact({ label, value, emphasis }: { label: string; value: string; emphasis?: "good" | "warning" }) {
  const tone = emphasis === "warning" ? "text-orange-600 dark:text-orange-300" : emphasis === "good" ? "text-emerald-600 dark:text-emerald-300" : "text-slate-900 dark:text-white";
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-slate-200 py-3 last:border-b-0 dark:border-slate-800/80">
      <span className="text-sm text-slate-600 dark:text-slate-400">{label}</span>
      <span className={`text-right text-sm font-medium ${tone}`}>{value}</span>
    </div>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-l border-slate-200 pl-4 first:border-l-0 first:pl-0 dark:border-slate-800">
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">{value}</p>
    </div>
  );
}

function Score({ value }: { value: number }) {
  const color = value >= 90 ? "bg-emerald-400" : value >= 75 ? "bg-amber-400" : "bg-rose-400";
  return (
    <div className="flex items-center justify-end gap-2">
      <span className="h-1.5 w-12 overflow-hidden bg-slate-200 dark:bg-slate-800">
        <span className={`block h-full ${color}`} style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
      </span>
      <span className="w-10 text-right font-medium text-slate-900 dark:text-white">{decimal.format(value)}%</span>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-5 py-8 text-sm text-slate-500">{children}</div>;
}

function ReliabilityVisual({ carriers }: { carriers: Array<Carrier & { score: number }> }) {
  if (!carriers.length) return <Empty>No carrier reliability data is available.</Empty>;
  return (
    <div className="space-y-3 px-5 py-4">
      {carriers.slice(0, 6).map((carrier, index) => (
        <div key={`${carrier.carrierName}-${index}`} className="grid grid-cols-[minmax(95px,1fr)_minmax(120px,2fr)_38px] items-center gap-3 text-xs">
          <span className="truncate font-medium text-slate-800 dark:text-slate-200" title={carrier.carrierName}>{carrier.carrierName || "Unknown"}</span>
          <span className="flex h-1.5 overflow-hidden bg-slate-200 dark:bg-slate-800">
            <span className="bg-sky-400" style={{ width: `${Math.max(0, Math.min(100, carrier.onTimeDeparturePct || 0))}%` }} />
            <span className="bg-slate-800" style={{ width: `${Math.max(0, 100 - (carrier.onTimeDeparturePct || 0))}%` }} />
          </span>
          <span className="text-right font-medium text-slate-900 dark:text-white">{decimal.format(carrier.score)}%</span>
        </div>
      ))}
    </div>
  );
}

function LaneVisual({ lanes }: { lanes: Lane[] }) {
  const maximum = Math.max(1, ...lanes.map((lane) => lane.shipmentCount || 0));
  if (!lanes.length) return <Empty>No trade-lane data is available.</Empty>;
  return (
    <div className="space-y-3 px-5 py-4">
      {lanes.slice(0, 6).map((lane, index) => {
        const volume = ((lane.shipmentCount || 0) / maximum) * 100;
        return <div key={`${lane.originPort}-${lane.destinationPort}-${index}`} className="grid grid-cols-[minmax(130px,1fr)_minmax(100px,2fr)_32px] items-center gap-3 text-xs">
          <span className="truncate text-slate-700 dark:text-slate-300" title={`${lane.originPort || "—"} → ${lane.destinationPort || "—"}`}>{lane.originPort || "—"} <span className="text-orange-500 dark:text-orange-400">→</span> {lane.destinationPort || "—"}</span>
          <span className="h-1.5 overflow-hidden bg-slate-200 dark:bg-slate-800"><span className="block h-full bg-orange-400" style={{ width: `${volume}%` }} /></span>
          <span className="text-right font-medium text-slate-900 dark:text-white">{int.format(lane.shipmentCount || 0)}</span>
        </div>;
      })}
    </div>
  );
}

export default function ShipmentAnalyticsPage({
  apiFetch,
  initialCustomerId,
  onSelectedCustomerChange,
  onPortalContext,
  onAccessDenied,
  onAdminToolbarChange,
}: Props) {
  const { portalKey: routePortalKey } = useParams<{ portalKey?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryCustomerId = searchParams.get("customerId") || undefined;
  const [selectedCustomerId, setSelectedCustomerId] = useState(initialCustomerId || queryCustomerId || "");
  const [bootstrap, setBootstrap] = useState<RuntimeBootstrap | null>(null);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [availableFilterOptions, setAvailableFilterOptions] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const lastLoadedCustomerRef = useRef("");
  const selectedCustomerIdRef = useRef(selectedCustomerId);
  const overviewLoadKeyRef = useRef("");
  const filterOptionsLoadKeyRef = useRef("");
  const filterDefaultsInitializedRef = useRef(false);
  const filterQuery = useMemo(() => {
    const query = new URLSearchParams();
    ["search", "status", "pol", "pod", "carrier", "vessel", "consignee", "modality", "year", "month"].forEach((key) => {
      const value = searchParams.get(key);
      if (value && value.toLowerCase() !== "all") query.set(key, value);
    });
    return query.toString();
  }, [searchParams]);

  useEffect(() => {
    if (filterDefaultsInitializedRef.current) return;
    filterDefaultsInitializedRef.current = true;
    const next = new URLSearchParams(searchParams);
    if (searchParams.has("modality") || searchParams.has("year")) {
      if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
      return;
    }
    next.set("modality", "OCEAN");
    next.set("year", String(new Date().getFullYear()));
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    selectedCustomerIdRef.current = selectedCustomerId;
  }, [selectedCustomerId]);

  useEffect(() => {
    if (selectedCustomerId) onSelectedCustomerChange?.(selectedCustomerId);
  }, [onSelectedCustomerChange, selectedCustomerId]);

  const requestHeaders = useCallback(
    (customerId?: string, canSelectCustomer = false) => {
      const headers = new Headers(routePortalKey ? { "X-Portal-Key": routePortalKey } : undefined);
      if (canSelectCustomer && customerId) headers.set("X-Customer-Id", customerId);
      return headers;
    },
    [routePortalKey],
  );

  useEffect(() => {
    if (!selectedCustomerId || !bootstrap) return;
    const cacheKey = `${routePortalKey || window.location.hostname}:${selectedCustomerId}`;
    const cached = getCachedAnalyticsFilterOptions<Record<string, string[]>>(cacheKey);
    if (cached) {
      setAvailableFilterOptions(cached);
      return;
    }
    if (filterOptionsLoadKeyRef.current === cacheKey) return;
    filterOptionsLoadKeyRef.current = cacheKey;
    let cancelled = false;
    apiFetch("/api/foundry/runtime/analytics/filter-options", { headers: requestHeaders(selectedCustomerId, Boolean(bootstrap?.canSelectCustomer)) })
      .then(async (response) => {
        if (!response.ok) throw new Error("Unable to load analytics filters.");
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        const nextOptions = data?.filterOptions && typeof data.filterOptions === "object" ? data.filterOptions : {};
        setAvailableFilterOptions(nextOptions);
        setCachedAnalyticsFilterOptions(cacheKey, nextOptions);
      })
      .catch(() => { if (!cancelled) setAvailableFilterOptions({}); })
      .finally(() => {
        if (filterOptionsLoadKeyRef.current === cacheKey) filterOptionsLoadKeyRef.current = "";
      });
    return () => { cancelled = true; };
  }, [apiFetch, bootstrap, requestHeaders, routePortalKey, selectedCustomerId]);

  const loadBootstrap = useCallback(async () => {
    const portalCacheKey = routePortalKey || window.location.hostname;
    const bootstrapCacheKey = getPortalBootstrapCacheKey(portalCacheKey, selectedCustomerIdRef.current);
    let nextBootstrap = getCachedPortalBootstrap<RuntimeBootstrap>(bootstrapCacheKey);
    if (!nextBootstrap) {
      const bootstrapResponse = await apiFetch("/api/foundry/runtime/bootstrap", { headers: requestHeaders(selectedCustomerIdRef.current, true) });
      if (bootstrapResponse.status === 401 || bootstrapResponse.status === 403) {
        onAccessDenied?.();
        return null;
      }
      if (!bootstrapResponse.ok) throw new Error("Unable to load portal access.");
      nextBootstrap = (await bootstrapResponse.json()) as RuntimeBootstrap;
      setCachedPortalBootstrap(bootstrapCacheKey, nextBootstrap);
    }
    setBootstrap(nextBootstrap);
    const contextCustomerId = selectedCustomerIdRef.current || nextBootstrap.selectedCustomer?.customerId || nextBootstrap.customers?.[0]?.customerId || "";
    onPortalContext?.({ role: nextBootstrap.role, customerId: contextCustomerId, enabledPages: nextBootstrap.enabledPages });
    return nextBootstrap;
  }, [apiFetch, onAccessDenied, onPortalContext, requestHeaders, routePortalKey]);

  const loadOverview = useCallback(async (customerId: string, canSelectCustomer: boolean, force = false) => {
    const cacheKey = `${routePortalKey || window.location.hostname}:${customerId}:${filterQuery}`;
    const cached = !force && customerId ? getCachedAnalyticsOverview<Overview>(cacheKey) : null;
    if (cached) {
      if (selectedCustomerIdRef.current !== customerId) return;
      setOverview(cached);
      lastLoadedCustomerRef.current = `${customerId}:${filterQuery}`;
      return;
    }
    if (overviewLoadKeyRef.current === cacheKey) return;
    overviewLoadKeyRef.current = cacheKey;
    try {
      const query = filterQuery ? `?${filterQuery}` : "";
      const response = await apiFetch(`/api/foundry/runtime/analytics/overview${query}`, {
        headers: requestHeaders(customerId, canSelectCustomer),
      });
      if (response.status === 401 || response.status === 403) {
        onAccessDenied?.();
        return;
      }
      if (!response.ok) throw new Error("Unable to load analytics.");
      const nextOverview = (await response.json()) as Overview;
      if (selectedCustomerIdRef.current !== customerId) return;
      setOverview(nextOverview);
      setCachedAnalyticsOverview(cacheKey, nextOverview);
      lastLoadedCustomerRef.current = `${customerId}:${filterQuery}`;
    } finally {
      if (overviewLoadKeyRef.current === cacheKey) overviewLoadKeyRef.current = "";
    }
  }, [apiFetch, filterQuery, onAccessDenied, requestHeaders, routePortalKey]);

  const loadPage = useCallback(async (force = false) => {
    setLoading(true);
    setError("");
    let requestedCustomerId = "";
    try {
      const nextBootstrap = await loadBootstrap();
      if (!nextBootstrap) return;
      const customers = nextBootstrap.customers || [];
      const customerId = selectedCustomerIdRef.current || nextBootstrap.selectedCustomer?.customerId || customers[0]?.customerId || "";
      requestedCustomerId = customerId;
      selectedCustomerIdRef.current = customerId;
      setSelectedCustomerId((current) => current || customerId);
      await loadOverview(customerId, Boolean(nextBootstrap.canSelectCustomer), force);
    } catch (cause) {
      if (requestedCustomerId && selectedCustomerIdRef.current !== requestedCustomerId) return;
      setError(cause instanceof Error ? cause.message : "Unable to load analytics.");
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [loadBootstrap, loadOverview]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  useEffect(() => {
    const loadedKey = `${selectedCustomerId}:${filterQuery}`;
    if (!bootstrap || !selectedCustomerId || lastLoadedCustomerRef.current === loadedKey) return;
    setLoading(true);
    setError("");
    loadOverview(selectedCustomerId, Boolean(bootstrap.canSelectCustomer))
      .catch((cause) => {
        if (selectedCustomerIdRef.current !== selectedCustomerId) return;
        setError(cause instanceof Error ? cause.message : "Unable to load analytics.");
        setOverview(null);
      })
      .finally(() => setLoading(false));
  }, [bootstrap, filterQuery, loadOverview, selectedCustomerId]);

  useEffect(() => {
    if (!bootstrap || !onAdminToolbarChange) return;
    const customers = bootstrap.customers || [];
    const customerId = selectedCustomerId || bootstrap.selectedCustomer?.customerId || customers[0]?.customerId || "";
    onAdminToolbarChange({
      visible: Boolean(bootstrap.canSelectCustomer),
      customerId,
      customers,
      loading,
      refreshing: false,
      onCustomerChange: setSelectedCustomerId,
      onReload: () => { void loadPage(true); },
      onRefresh: async () => undefined,
    });
  }, [bootstrap, loading, loadPage, onAdminToolbarChange, selectedCustomerId]);

  /*
   * Bootstrap is intentionally separate from the selected-customer request.
   * Updating the toolbar must not trigger a new bootstrap call; only the
   * overview is reloaded when an administrator changes customer.
   */

  const customerName = bootstrap?.customers?.find((customer) => customer.customerId === selectedCustomerId)?.customerName || bootstrap?.selectedCustomer?.customerName || "Customer";
  const carriers = useMemo(
    () => (overview?.carrierPerformance || [])
      .filter((carrier) => {
        const name = carrier.carrierName?.trim().toLocaleLowerCase();
        return Boolean(name && name !== "unknown");
      })
      .slice(0, 12)
      .map((carrier) => ({
        ...carrier,
        score: average([carrier.onTimeDeparturePct || 0, carrier.onTimeEtaPct || 0]),
      })),
    [overview],
  );
  const healthScore = average(carriers.map((carrier) => carrier.score));
  const onTimeEta = average(carriers.map((carrier) => carrier.onTimeEtaPct || 0));
  const lateEta = Math.max(0, 100 - onTimeEta);
  const lowestCarrier = [...carriers].sort((a, b) => a.score - b.score)[0];
  const lanes = (overview?.volumeAnalysis || []).slice(0, 12);
  const laneActivity = useMemo(() => {
    const byLane = new Map<string, Lane>();
    for (const lane of overview?.volumeAnalysis || []) {
      const originPort = lane.originPort || "â€”";
      const destinationPort = lane.destinationPort || "â€”";
      const key = `${originPort}\u0000${destinationPort}`.toLocaleLowerCase();
      const current = byLane.get(key) || { originPort, destinationPort };
      current.shipmentCount = (current.shipmentCount || 0) + (lane.shipmentCount || 0);
      current.containerCount = (current.containerCount || 0) + (lane.containerCount || 0);
      current.grossWeight = (current.grossWeight || 0) + (lane.grossWeight || 0);
      current.volume = (current.volume || 0) + (lane.volume || 0);
      byLane.set(key, current);
    }
    return [...byLane.values()]
      .sort((left, right) => (right.shipmentCount || 0) - (left.shipmentCount || 0))
      .slice(0, 12);
  }, [overview]);
  const executive = overview?.executive;
  const available = (key: string, fallback: string[]) => Array.from(new Set([...(availableFilterOptions[key] || []), ...fallback])).sort();
  const currentYear = new Date().getFullYear();
  const selectableYears = Array.from({ length: 8 }, (_value, index) => String(currentYear + 1 - index));
  const filterOptions = {
    status: available("status", []), pol: available("pol", []), pod: available("pod", []), carrier: available("carrier", []), vessel: available("vessel", []), consignee: available("consignee", []), modality: available("modality", ["OCEAN", "AIR", "ROAD", "RAIL"]),
    year: Array.from(new Set([...(availableFilterOptions.year || []), ...selectableYears])).sort((a, b) => Number(b) - Number(a)),
    month: available("month", ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"]),
  };
  const filters = [
    ["Status", "status", filterOptions.status], ["POL", "pol", filterOptions.pol], ["POD", "pod", filterOptions.pod], ["Carrier", "carrier", filterOptions.carrier], ["Vessel", "vessel", filterOptions.vessel], ["Consignee", "consignee", filterOptions.consignee], ["Modality", "modality", filterOptions.modality], ["Year", "year", filterOptions.year], ["Month", "month", filterOptions.month],
  ] as Array<[string, string, string[]]>;

  const setFilter = (key: string, value: string | string[]) => {
    const next = new URLSearchParams(searchParams);
    const serialized = Array.isArray(value) ? value.join("|") : value;
    if (!serialized || serialized === "all") next.delete(key); else next.set(key, serialized);
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    ["search", "status", "pol", "pod", "carrier", "vessel", "consignee", "modality", "year", "month"].forEach((key) => next.delete(key));
    setSearchParams(next, { replace: true });
  };

  const sharedFilters: ShipmentFilterBarFilter[] = filters.map(([label, key, options]) => ({
    label,
    options,
    value: parseFilterValues(searchParams.get(key) || (key === "modality" ? "OCEAN" : key === "year" ? String(new Date().getFullYear()) : "")),
    onChange: (value) => setFilter(key, value),
  }));

  return (
    <main className="w-full">
      <section className="overflow-hidden border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/60">
        <div className="h-0.5 bg-gradient-to-r from-orange-400 via-sky-400 to-emerald-400" />
        <div className="grid divide-y divide-slate-200 dark:divide-slate-800 md:grid-cols-[minmax(230px,1.4fr)_repeat(5,minmax(132px,1fr))] md:divide-x md:divide-y-0">
          <div className="px-5 py-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-orange-400">Shipment intelligence</p>
            <h1 className="mt-1 truncate text-xl font-semibold text-slate-900 dark:text-white" title={customerName}>{customerName}</h1>
          </div>
          <Kpi label="Health score" value={`${decimal.format(healthScore)}%`} Icon={Activity} accent="#34d399" calculation="Average carrier score. Each carrier score is the average of its on-time departure and on-time ETA percentages, based on originalEtd, etd, originalEta and eta." />
          <Kpi label="Shipments" value={int.format(executive?.totalShipments || 0)} Icon={Boxes} accent="#38bdf8" calculation="Distinct shipment_id count across the loaded shipment records." />
          <Kpi label="Containers" value={int.format(executive?.totalContainers || 0)} Icon={Container} accent="#a78bfa" calculation="Distinct container_id count linked to the loaded shipment records." />
          <Kpi label="Active carriers" value={int.format(executive?.totalCarriers || 0)} Icon={Users} accent="#f59e0b" calculation="Distinct non-empty carrierName count in the master shipment records." />
          <Kpi label="Late ETA" value={`${decimal.format(lateEta)}%`} Icon={Clock3} accent="#f43f5e" calculation="100% minus the average on-time ETA percentage across carriers. On-time ETA means eta is on or before originalEta." />
        </div>
      </section>

      <ShipmentFilterBar filters={sharedFilters} search={searchParams.get("search") || ""} onSearchChange={(value) => setFilter("search", value)} onClear={clearFilters} />

      {error ? <div className="mt-5 border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{error}</div> : null}

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Panel title="Operational alerts" calculation="Highlights the carrier with the lowest reliability score. The score averages on-time ETD and ETA rates; shipment count uses distinct shipment_id.">
          <div className="px-5">
            <Fact label="Lowest-performing carrier" value={lowestCarrier?.carrierName || "No carrier data"} emphasis="warning" />
            <Fact label="Carrier score / shipments" value={lowestCarrier ? `${decimal.format(lowestCarrier.score)}% · ${int.format(lowestCarrier.shipmentCount || 0)}` : "—"} />
          </div>
        </Panel>
        <Panel title="Portfolio summary" calculation="Average transit uses ETD and ETA. On-time ETA compares eta to originalEta. Gross weight and volume sum container grossWeight and volume fields.">
          <div className="grid grid-cols-2 gap-x-5 gap-y-5 px-5 py-5 sm:grid-cols-4">
            <Summary label="Average transit" value={`${decimal.format(executive?.avgTransitDays || 0)} days`} />
            <Summary label="On-time ETA" value={`${decimal.format(onTimeEta)}%`} />
            <Summary label="Gross weight" value={`${int.format(executive?.totalGrossWeight || 0)} kg`} />
            <Summary label="Volume" value={`${decimal.format(executive?.totalVolume || 0)} m³`} />
          </div>
        </Panel>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Panel title="Carrier reliability" calculation="Shows each carrier's on-time departure rate from originalEtd and etd. The displayed score averages that rate with the on-time ETA rate from originalEta and eta."><ReliabilityVisual carriers={carriers} /></Panel>
        <Panel title="Lane activity" calculation="Groups records by originPort and destinationPort, then ranks lanes by distinct shipment_id count. It uses the master shipment routing fields."><LaneVisual lanes={laneActivity} /></Panel>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Panel title="Carrier performance" calculation="Groups by carrierName. It counts distinct shipment_id and container_id, averages ETD-to-ETA transit time, and compares etd/originalEtd and eta/originalEta for on-time rates.">
          {loading ? <Empty>Loading carrier performance…</Empty> : carriers.length === 0 ? <Empty>No carrier performance data is available.</Empty> : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-xs">
                <thead className="border-b border-slate-200 text-[10px] uppercase tracking-[0.1em] text-slate-500 dark:border-slate-800">
                  <tr>
                    <th className="px-5 py-3 font-medium">Carrier</th><th className="px-3 py-3 text-right font-medium">Shipments</th><th className="px-3 py-3 text-right font-medium">Containers</th><th className="px-3 py-3 text-right font-medium">Transit</th><th className="px-3 py-3 text-right font-medium">ETD</th><th className="px-5 py-3 text-right font-medium">ETA / score</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800/80">
                  {carriers.map((carrier, index) => <tr key={`${carrier.carrierName}-${index}`} className="text-slate-600 dark:text-slate-300"><td className="whitespace-nowrap px-5 py-3 font-medium text-slate-900 dark:text-white">{carrier.carrierName || "Unknown"}</td><td className="px-3 py-3 text-right">{int.format(carrier.shipmentCount || 0)}</td><td className="px-3 py-3 text-right">{int.format(carrier.containerCount || 0)}</td><td className="px-3 py-3 text-right">{decimal.format(carrier.avgTransitDays || 0)}d</td><td className="px-3 py-3 text-right">{decimal.format(carrier.onTimeDeparturePct || 0)}%</td><td className="px-5 py-3 text-right"><div className="flex items-center justify-end gap-3"><span>{decimal.format(carrier.onTimeEtaPct || 0)}%</span><Score value={carrier.score} /></div></td></tr>)}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
        <Panel title="Trade-lane volume" calculation="Groups by carrierName, originPort and destinationPort. It counts distinct shipment_id and container_id, and sums container grossWeight and volume.">
          {loading ? <Empty>Loading trade-lane volume…</Empty> : lanes.length === 0 ? <Empty>No trade-lane volume data is available.</Empty> : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-xs">
                <thead className="border-b border-slate-200 text-[10px] uppercase tracking-[0.1em] text-slate-500 dark:border-slate-800"><tr><th className="px-5 py-3 font-medium">Carrier / lane</th><th className="px-3 py-3 text-right font-medium">Shipments</th><th className="px-3 py-3 text-right font-medium">Containers</th><th className="px-3 py-3 text-right font-medium">Weight</th><th className="px-5 py-3 text-right font-medium">Volume</th></tr></thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800/80">
                  {lanes.map((lane, index) => <tr key={`${lane.carrierName}-${lane.originPort}-${lane.destinationPort}-${index}`} className="text-slate-600 dark:text-slate-300"><td className="px-5 py-3"><p className="whitespace-nowrap font-medium text-slate-900 dark:text-white">{lane.carrierName || "Unknown"}</p><p className="mt-0.5 whitespace-nowrap text-[11px] text-slate-500">{lane.originPort || "—"} <span className="text-sky-500 dark:text-sky-400">→</span> {lane.destinationPort || "—"}</p></td><td className="px-3 py-3 text-right">{int.format(lane.shipmentCount || 0)}</td><td className="px-3 py-3 text-right">{int.format(lane.containerCount || 0)}</td><td className="px-3 py-3 text-right">{int.format(lane.grossWeight || 0)} kg</td><td className="px-5 py-3 text-right">{decimal.format(lane.volume || 0)} m³</td></tr>)}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </main>
  );
}
